import styles from './SessionsAuthHeader.module.css'
import StaticGithubAvatar from './Components/StaticGithubAvatar'
import StaticUserAvatar from './Components/StaticUserAvatar'
import AnimatedUserAvatar from './Components/AnimatedUserAvatar'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import type {SafeHTMLString} from '@github-ui/safe-html'

/**
 * Props for SessionsAuthHeader component.
 */
export interface SessionsAuthHeaderProps {
  /** The URL of the user's avatar image. */
  avatarUrl?: string
  /** The user's display name. */
  userName?: string
  /** The header title. */
  title?: string
  /** The header description. */
  description?: string | SafeHTMLString
  /** The previous path, used for animation logic. */
  prevPath?: string | null | undefined
  /**
   * The variant of the avatar to display.
   * - 'github': Displays a static GitHub icon.
   * - 'user': Displays a static user avatar.
   * - 'animated': Displays an animated user avatar that changes based on the referrer path: /login.
   */
  variant: 'github' | 'user' | 'animated'
}

const SessionsAuthHeaderContent = ({title, description}: {title?: string; description?: string | SafeHTMLString}) => {
  const isHTMLString = typeof description === 'string' && description.includes('<')
  return (
    <div className={styles.authFormHeaderContent}>
      <h1 className={styles.authFormHeaderTitle}>{title}</h1>
      {description ? (
        isHTMLString ? (
          /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- TODO: [SECURITY] Potential XSS risk. Pre-existing infraction - DO NOT COPY. Must be fixed. */
          <VerifiedHTMLText as="p" className={styles.authFormParagraph} html={description as SafeHTMLString} />
        ) : (
          description
            .split('\n')
            .filter(line => line.trim() !== '')
            .map(line => (
              <p className={styles.authFormParagraph} key={`line-${line.slice(0, 20)}-${line.length}`}>
                {line}
              </p>
            ))
        )
      ) : null}
    </div>
  )
}

type SessionsAuthHeaderAvatarProps = {
  variant: 'github' | 'user' | 'animated'
  prevPath?: string | null | undefined
  avatarUrl?: string
  userName?: string
}

const SessionsAuthHeaderAvatar = ({prevPath, variant, avatarUrl, userName}: SessionsAuthHeaderAvatarProps) => {
  switch (variant) {
    case 'github':
      return <StaticGithubAvatar />
    case 'animated': {
      const shouldAnimateOnLoginReferrer = /^\/login\/?$/.test(prevPath ?? '')

      if (shouldAnimateOnLoginReferrer) {
        return <AnimatedUserAvatar avatarUrl={avatarUrl ?? ''} userName={userName ?? ''} />
      } else {
        return <StaticUserAvatar avatarUrl={avatarUrl ?? ''} userName={userName ?? ''} />
      }
    }
    case 'user':
      return <StaticUserAvatar avatarUrl={avatarUrl ?? ''} userName={userName ?? ''} />
    default:
      return <StaticGithubAvatar />
  }
}

export function SessionsAuthHeader({
  avatarUrl,
  userName,
  title,
  description,
  prevPath,
  variant,
}: SessionsAuthHeaderProps) {
  return (
    <div className={styles.authFormHeader}>
      <SessionsAuthHeaderAvatar avatarUrl={avatarUrl} userName={userName} prevPath={prevPath} variant={variant} />
      <SessionsAuthHeaderContent title={title} description={description} />
    </div>
  )
}
